<script>
import ZoomImage from '@/components/ZoomImage.vue';
import { MedicalCardRejectedNotification } from '@/storage/gateways/NotificationGateway';
import PhoneNumbersToLinks from '@/components/PhoneNumbersToLinks.vue';

/**
 * @description Общие входные параметры уведомлений.
 * @type {Object}
 */
const notificationBodyProps = {
  /**
   * @description Текст тела.
   * @type {string}
   */
  body: { required: true, type: String },
};

const prepareText = (createElement, body) => {
  const blocks = body.split('\n');
  return blocks.map((block) => {
    const element = createElement(PhoneNumbersToLinks, {
      props: { text: block },
    }, block);
    element.data.style = { margin: '0px' };
    return element;
  });
};

const makeBody = (createElement, body) => createElement(
  'div',
  { class: 'notification-item-body__text' },
  [prepareText(createElement, body)],
);

/**
 * @description Тело уведомления по умолчанию.
 * @type {Vue}
 */
const DefaultNotificationBody = {
  functional: true,
  name: 'DefaultNotificationBody',
  props: {
    ...notificationBodyProps,
    /**
     * @description Ссылка на изображение.
     * @type {?string}
     */
    imageURL: { default: () => null, required: true, type: String },
    /**
     * @description Ссылка на видео.
     * @type {?string}
     */
    videoURL: { default: () => null, required: true, type: String },
  },
  render(createElement, { props }) {
    const children = [makeBody(createElement, props.body)];
    if (props.imageURL) {
      children.push(
        createElement(ZoomImage, {
          class: 'notification-item-body__img',
          props: {
            src: props.imageURL,
          },
        }),
      );
    }
    if (props.videoURL) {
      children.push(
        createElement('iframe', {
          class: 'notification-item-body__iframe',
          attrs: {
            src: props.videoURL,
            title: 'YouTube video player',
            frameborder: '0',
            allow: `accelerometer; autoplay; clipboard-write;
            encrypted-media; gyroscope; picture-in-picture`,
            allowfullscreen: true,
          },
        }),
      );
    }
    return createElement('div', { class: 'notification-item-body__content' }, children);
  },
};

/**
 * @description Тело уведомления об отклоненной глав. врачом медицинской карты (визите).
 * @type {Vue}
 */
const MedicalCardRejectedNotificationBody = {
  functional: true,
  name: 'MedicalCardRejectedNotificationBody',
  props: {
    ...notificationBodyProps,
    /**
     * @description Идентификатор пациента.
     * @type {number}
     */
    patientId: { required: true, type: Number },
    /**
     * @description Идентификатор визита.
     * @type {number}
     */
    visitId: { required: true, type: Number },
  },
  render(createElement, { props }) {
    const bodyElement = makeBody(createElement, props.body);
    const linkProps = {
      to: {
        name: 'ClinicPanelPatientVisit',
        params: {
          id: props.patientId,
          visitId: props.visitId,
        },
      },
    };
    const visitLinkElement = createElement('router-link', { props: linkProps }, 'Перейти');
    return createElement(
      'div',
      { class: 'notification-item-body__content' },
      [bodyElement, visitLinkElement],
    );
  },
};

export default {
  functional: true,
  name: 'NotificationItemBody',
  props: {
    notification: { required: true, type: Object },
  },
  render(createElement, { props }) {
    if (props.notification instanceof MedicalCardRejectedNotification) {
      const notificationProps = {
        body: props.notification.base.body,
        patientId: props.notification.patientId,
        visitId: props.notification.visitId,
      };
      return createElement(MedicalCardRejectedNotificationBody, { props: notificationProps });
    }
    const notificationProps = {
      body: props.notification.base.body,
      videoURL: props.notification.videoURL,
      imageURL: props.notification.imageURL,
    };
    return createElement(DefaultNotificationBody, { props: notificationProps });
  },
  components: {
    PhoneNumbersToLinks,
  },
};
</script>

<style  lang="scss">
.notification-item-body {
  &__content {
    cursor: auto;
    display: grid;
    grid-template-columns: 1fr;
    grid-gap: 10px;
    margin: 5px 0 10px 0;
    a:hover {
      text-decoration: none;
    }
  }
  &__text {
    font-size: 16px;
    margin: 0;
    white-space: pre-wrap;
    p {
      display: block;
    }
  }
  &__img {
    height: 250px;
    width: 300px;
    overflow: hidden;
    img {
      max-height: 100%;
      max-width: 100%;
      object-fit: contain;
      border-radius: 4px;
      overflow: hidden;
    }
  }
  &__iframe {
    width: 560px;
    max-width: 100%;
    height: 315px;
    border-radius: 4px;
    overflow: hidden;
  }
}

@media screen and (max-width: 480px) {
  .notification-item-body {
    &__img {
      width: 100%;
      height: 200px;
    }
    &__iframe {
      width: 100%;
      height: 200px;
    }
  }
}
</style>
